import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den Ausschreiben-Dialog
 * (modules/admin/component/publish-open-shifts-dialog →
 * PublishOpenShiftsDialogComponent). Geoeffnet ueber die Bedarf-Tooltip-Aktion
 * "Ausschreiben" im Schichtplaner (Klick auf eine offene Anforderung →
 * Popover → "Ausschreiben"). Wird via Hilfe-Button
 * (`<app-dialog-help-button>`) im Hero-Header manuell gestartet. Erklaert in
 * 6 Schritten: Welcome → Positionen & Stellenzahl → Bewerbungen verwalten →
 * Manager-Freigabe → Empfaenger → Ausschreiben.
 *
 * Pattern analog zu `SHIFT_APPLICATIONS_INBOX_HELP_TOUR` /
 * `ADD_SHIFT_HELP_TOUR`: erklaerungs-getrieben (manuelles Weiterklicken,
 * `nextCondition: 'manual'`), nicht formular-gebunden.
 *
 * Datenabhaengigkeit: Die Bewerber-Sektion (.pos-applicants) rendert nur nach
 * Veroeffentlichung (eine Position hat Bewerber), die Empfaenger-Sektion nur
 * wenn qualifizierte/verfuegbare Mitarbeiter existieren (*ngIf
 * availableEmployees.length>0). Die Schritte `applicants` und `receivers`
 * skippen daher ueber `skipIf` (DOM-Existenz-Check) — im Entwurfsmodus laeuft
 * die Tour ohne den Bewerber-Step.
 *
 * Fachliche Quelle (Backend): `Application/ShiftSwap/Services/OpenShiftService.cs`
 * + `PublishRequirementAsOpenShiftsCommandHandler.cs`:
 * - Pro offener Stelle entsteht EIN OpenShift (Status Open); max je Position =
 *   OptimalCount − bereits besetzt − bereits ausgeschrieben.
 * - ManagerApprovalRequired=true → Bewerbung bleibt Claimed bis zur Bestaetigung;
 *   =false → erste Bewerbung wird sofort gefuellt (WorkShift erzeugt, Status Filled).
 * - Benachrichtigt werden nur qualifizierte (Skill + zur Schichtzeit frei) MA;
 *   ausgeschlossene (excludedEmployeeIds) landen in RejectedEmployeeIds und
 *   koennen sich nicht bewerben.
 * - Annehmen (ApproveClaim) erzeugt die WorkShift; Ablehnen (RejectClaim) gibt
 *   die Stelle wieder frei und sperrt den MA; Zuruecknehmen (Cancel) storniert
 *   die Ausschreibung der Position.
 */

export const PUBLISH_OPEN_SHIFTS_HELP_TOUR: TourConfig = {
  id: TourFeature.PublishOpenShiftsHelp,
  version: 1,
  title: 'PUBLISH_OPEN_SHIFTS_HELP_TOUR_TITLE',
  description: 'PUBLISH_OPEN_SHIFTS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Schichtzuweisung navigieren. Der Ausschreiben-Dialog
  // hat KEINEN deterministischen Opener-Button — er oeffnet ueber das
  // Bedarf-Tooltip-Popover (Klick auf eine offene Anforderung). Daher `route`
  // only, ohne `opens`. Aus dem In-Dialog-`?`-Button uebernimmt der bestehende
  // Pfad (openDialogs.length > 0).
  launchContext: {
    route: '/planner',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 80,
  // Bewusst 'feature-discovery' (NICHT 'contextual-help'): klassische Guided
  // Tour mit Spotlight + Voice-Over, nicht der HelpModeService mit (i)-Icons.
  // Begruendung siehe AUTO_SUGGEST_HELP_TOUR.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'PUBLISH_OPEN_SHIFTS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight am .publish-dialog-Wrapper,
          // daher kein zusaetzlicher Cue (vermeidet Doppelring).
          anchor: AnchorId.PublishOpenShiftsDialog,
          title: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.WELCOME.TITLE',
          content: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'positions',
          anchor: AnchorId.PublishOpenShiftsPositions,
          title: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.POSITIONS.TITLE',
          content: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.POSITIONS.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'applicants',
          anchor: AnchorId.PublishOpenShiftsApplicants,
          title: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.APPLICANTS.TITLE',
          content: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.APPLICANTS.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // .pos-applicants existiert nur, wenn eine Position bereits Bewerber
          // hat (*ngIf="pos.applicants.length > 0"). Im Entwurfsmodus fehlt der
          // Anchor → Step wird uebersprungen.
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.PublishOpenShiftsApplicants}"]`
            ),
        },
        {
          id: 'approval',
          anchor: AnchorId.PublishOpenShiftsApproval,
          title: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.APPROVAL.TITLE',
          content: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.APPROVAL.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'receivers',
          anchor: AnchorId.PublishOpenShiftsReceivers,
          title: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.RECEIVERS.TITLE',
          content: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.RECEIVERS.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Sektion ist *ngIf="availableEmployees.length > 0".
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.PublishOpenShiftsReceivers}"]`
            ),
        },
        {
          id: 'publish',
          anchor: AnchorId.PublishOpenShiftsSubmit,
          title: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.PUBLISH.TITLE',
          content: 'TUTORIAL.PUBLISH_OPEN_SHIFTS.PUBLISH.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Letzter Step: sauberer Abschluss, kein Zurueck-Button.
          showPrevButton: false,
        },
      ],
    },
  ],
};
